import React, { useState, useCallback, useEffect } from 'react';
import { Panel } from '../panels/Panel';
import { ConsoleToolbar } from './ConsoleToolbar';
import { ConsoleMessageList } from './ConsoleMessageList';
import { ConsoleCommandInput } from './ConsoleCommandInput';
import { ConsoleSettings } from './ConsoleSettings';
import { ConsoleContextMenu } from './ConsoleContextMenu';
import { useConsoleStore } from '../../store/consoleStore';
import { usePanelStore } from '../../store/panelStore';
import { useConsoleInterceptor } from '../../hooks/useConsoleInterceptor';
import { useConsoleMessages } from '../../hooks/useConsoleMessages';

// Bottom resize handle component - dispatches resize events to EditorLayout
const BottomResizeHandle: React.FC = () => {
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    let lastY = e.clientY;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = lastY - moveEvent.clientY; // Inverted: dragging up = larger
      lastY = moveEvent.clientY;

      // Dispatch custom event for EditorLayout to handle
      window.dispatchEvent(new CustomEvent('console:resize', { detail: { delta } }));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, []);

  return (
    <div
      className="h-2 cursor-ns-resize hover:bg-blue-500/30 transition-colors flex-shrink-0"
      onMouseDown={handleMouseDown}
    />
  );
};

export const ConsolePanel: React.FC = () => {
  const panel = usePanelStore((state) => state.panels['console']);
  const isSettingsOpen = useConsoleStore((state) => state.isSettingsOpen);
  const toggleSettings = useConsoleStore((state) => state.toggleSettings);
  const clear = useConsoleStore((state) => state.clear);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    messageId?: string;
  } | null>(null);

  // Intercept console.* calls
  useConsoleInterceptor(true);

  // Console message utilities
  const { navigateToCode } = useConsoleMessages();

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+L to clear console
      if ((e.ctrlKey || e.metaKey) && e.key === 'l') {
        e.preventDefault();
        clear();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clear]);

  // Handle context menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const target = e.target as HTMLElement;
    const messageElement = target.closest('[data-message-id]');
    const messageId = messageElement?.getAttribute('data-message-id') || undefined;

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      messageId,
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  if (!panel) return null;

  return (
    <Panel panel={panel}>
      <div
        className="flex flex-col h-full bg-zinc-900 relative"
        onContextMenu={handleContextMenu}
      >
        {/* Toolbar */}
        <ConsoleToolbar />

        {/* Message list */}
        <ConsoleMessageList onNavigateToCode={navigateToCode} />

        {/* Command input */}
        <ConsoleCommandInput />

        {/* Bottom resize handle - only for docked panels */}
        {panel.state === 'normal' && panel.position === 'bottom' && (
          <BottomResizeHandle />
        )}

        {/* Settings dialog */}
        {isSettingsOpen && <ConsoleSettings onClose={toggleSettings} />}

        {/* Context menu */}
        {contextMenu && (
          <ConsoleContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            messageId={contextMenu.messageId}
            onClose={closeContextMenu}
          />
        )}
      </div>
    </Panel>
  );
};
